import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, BellOff, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
    pushSupported,
    subscribeToProduct,
    unsubscribeFromProduct,
    isSubscribedFor,
} from "../lib/push";

/**
 * Renders a "Notify me when available" widget for products that are
 * sold-out or in Pre-ordine state.
 */
export default function NotifyMeButton({ product }) {
    const [supported, setSupported] = useState(false);
    const [subscribed, setSubscribed] = useState(false);
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setSupported(pushSupported());
        if (pushSupported() && product?.handle) {
            isSubscribedFor(product.handle).then(setSubscribed);
        }
    }, [product?.handle]);

    if (!product) return null;
    const shouldShow =
        !product.available || product.rarity === "Pre-ordine";
    if (!shouldShow) return null;

    async function activate() {
        setLoading(true);
        try {
            await subscribeToProduct({
                productHandle: product.handle,
                productTitle: product.character || product.name,
                email: email || null,
            });
            setSubscribed(true);
            setOpen(false);
            toast.success("Ti avviseremo quando disponibile ⚓");
        } catch (e) {
            toast.error(e.message || "Impossibile attivare le notifiche");
        } finally {
            setLoading(false);
        }
    }

    async function disable() {
        setLoading(true);
        try {
            await unsubscribeFromProduct(product.handle);
            setSubscribed(false);
            toast.success("Notifiche disattivate");
        } finally {
            setLoading(false);
        }
    }

    if (!supported) {
        return (
            <div className="mt-4 bounty-card p-4 text-xs font-mono text-ink/60" data-testid="notify-unsupported">
                Le notifiche push non sono supportate su questo browser.
            </div>
        );
    }

    return (
        <>
            <div className="mt-4">
                {subscribed ? (
                    <button
                        onClick={disable}
                        disabled={loading}
                        className="btn-manga btn-manga-gold w-full"
                        data-testid="notify-disable-btn"
                    >
                        <Bell size={16} className="mr-2 fill-current" />
                        Notifiche attive · disattiva
                    </button>
                ) : (
                    <button
                        onClick={() => setOpen(true)}
                        className="btn-manga btn-manga-red w-full"
                        data-testid="notify-open-btn"
                    >
                        <BellOff size={16} className="mr-2" />
                        Avvisami quando disponibile
                    </button>
                )}
            </div>

            <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setOpen(false)}
                            className="fixed inset-0 bg-ink/70 z-[60]"
                        />
                        <motion.div
                            initial={{ y: 40, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 40, opacity: 0 }}
                            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] sm:w-[440px] z-[70] bg-parchment border-2 border-ink shadow-manga-lg"
                            data-testid="notify-modal"
                        >
                            <div className="p-5 border-b-2 border-ink bg-ink text-parchment flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Bell size={20} className="text-gold" />
                                    <div>
                                        <div className="font-hand text-sm text-gold -mb-1">
                                            allarme pirata
                                        </div>
                                        <div className="font-display text-xl tracking-tight">
                                            AVVISAMI SULL&apos;ARRIVO
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setOpen(false)}
                                    className="w-8 h-8 border-2 border-parchment flex items-center justify-center hover:bg-bounty"
                                    aria-label="Chiudi"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                            <div className="p-6">
                                <p className="text-sm text-ink/80 leading-relaxed">
                                    Riceverai una notifica push direttamente sul
                                    tuo dispositivo quando{" "}
                                    <span className="font-bold">
                                        {product.character || product.name}
                                    </span>{" "}
                                    sarà di nuovo disponibile.
                                </p>
                                <input
                                    type="email"
                                    placeholder="Email (opzionale, per backup)"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input-manga mt-4 text-sm"
                                    data-testid="notify-email-input"
                                />
                                <div className="mt-3 text-xs font-mono text-ink/60 flex items-start gap-2">
                                    <Sparkles
                                        size={14}
                                        className="text-bounty flex-shrink-0 mt-0.5"
                                    />
                                    <span>
                                        Il browser ti chiederà il permesso per
                                        le notifiche. Puoi disattivarle in
                                        qualsiasi momento.
                                    </span>
                                </div>
                                <button
                                    onClick={activate}
                                    disabled={loading}
                                    className="btn-manga btn-manga-red w-full mt-5"
                                    data-testid="notify-activate-btn"
                                >
                                    {loading
                                        ? "Attivazione..."
                                        : "Attiva notifiche"}
                                </button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}
